import { useNavigation } from "@react-navigation/native"
import {
  Body2,
  Button,
  Caption,
  DateTimePicker,
  firestore,
  generateShadow,
  IRowItemProps,
  ISectionHeader,
  IUserContext,
  openUrl,
  ParametersScreen,
  useAlert,
  userContext,
  useTheme,
  VectorIcon,
  VectorIconProps,
} from "capsule"
import dayjs from "dayjs"
import firebase from "firebase/compat/app"
import { useFormikContext } from "formik"
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useCollectionData } from "react-firebase-hooks/firestore"
import { useTranslation } from "react-i18next"
import {
  Image,
  ImageSourcePropType,
  SectionListData,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native"

import { images } from "../../assets/images"
import { collections } from "../../common/types"
import SubmitButton from "../../components/SubmitButton"
import { ICON_SIZE, INPUT_HEIGHT } from "../../features/config/Constants"
import useItemI18n from "../../features/hooks/useItemI18n"
import useLocationPermission from "../../features/hooks/useLocationPermission"
import { OperationNS } from "../../features/i18n/constants"
import { usePhases } from "../../features/models/PhaseAndSessionFunctions"
import { AppUserData, Practician } from "../../features/models/UserData"
import { updateUserData } from "../../features/models/UserDataFunctions"
import { programContext } from "../../features/Providers/ProgramProvider"
import crashlytics from "../../services/Analytics/crashlytics"
import { getUtcDate } from "../../utils/conversion"
import { OperationFormValues } from "./Constants"
import { OperationParamList } from "./OperationNavigator"

interface IProps {
  isEdit?: boolean
}

const OperationScreen: FC<IProps> = ({ isEdit = false }) => {
  const {
    fontMaker,
    colors: {
      accent,
      surface,
      white: { highEmphasis: white },
      black: { mediumEmphasis: black },
      primary,
    },
    dimensions: { spacing },
  } = useTheme()
  const { hasPermission, requestLocationPermission } = useLocationPermission()

  useEffect(() => {
    if (hasPermission === null && !isEdit) {
      requestLocationPermission()
    }
  }, [hasPermission, requestLocationPermission, isEdit])
  const { t } = useTranslation(OperationNS)
  const navigation = useNavigation<any>()
  const { showSnack } = useAlert()
  const { handleSubmit, values, validateForm, setFieldValue } =
    useFormikContext<OperationFormValues>()
  const { userData, userDocRef } = useContext<IUserContext<AppUserData>>(userContext) // patient data
  const { phases, currentPhase } = useContext(programContext)
  const firstActivation = !userData?.pathology && !!values.pathology
  const startPhase = usePhases(values.surgeryDate, "asc", firstActivation, values.pathology)
  const endPhase = usePhases(values.surgeryDate, "desc", firstActivation, values.pathology)
  const currentDate = useMemo(() => getUtcDate(undefined, true), [])
  const query = useMemo(() => {
    if (userData?.practiciansRequested && userData.practiciansRequested.length > 0) {
      return firestore()
        .collection(collections.LOGIN)
        .where(
          firestore.FieldPath.documentId(),
          "in",
          userData.practiciansRequested,
        ) as unknown as firebase.firestore.Query<Practician>
    }
    return null
  }, [userData])
  const [practicianDocs] = useCollectionData<Practician>(query, { idField: "id" })
  const requestedKine = practicianDocs?.find(tm => tm.role === "kine")
  const pathologyName = useItemI18n(
    collections.PATHOLOGIES,
    (userData?.pathology as string) ?? values.pathology,
    "name",
  )
  const isPracticianRequested =
    userData?.practiciansRequested && userData?.practiciansRequested.length > 0
  const practicianRequestedText = isPracticianRequested ? t("requestSent") : t("missing")

  const [submitLoading, setSubmitLoading] = useState(false)
  const [initialStartPhaseId, setInitialStartPhaseId] = useState(startPhase?.id)

  useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    validateForm()
  }, [values, validateForm])

  useEffect(() => {
    if (isEdit && userData?.specialty) {
      setFieldValue("specialty", userData.specialty)
    }
  }, [isEdit, setFieldValue, userData?.specialty])

  const s = useMemo(
    () => ({
      header: {
        backgroundColor: surface.background,
      },
      row: [
        styles.row,
        {
          paddingVertical: spacing,
          paddingLeft: spacing * 2,
          marginTop: spacing / 4,
        },
      ],
      rowKine: [
        styles.shadow,
        {
          paddingHorizontal: spacing,
          paddingVertical: spacing / 2,
        },
      ],
      rowDate: {
        paddingHorizontal: spacing,
        paddingVertical: spacing / 2,
        backgroundColor: white,
      },
      rowSubitem: [
        styles.row,
        styles.shadow,
        {
          paddingLeft: spacing * 2,
          marginTop: spacing / 4,
        },
      ],
      rowPatInfo: [
        styles.shadow,
        {
          paddingHorizontal: spacing,
          paddingVertical: spacing / 2,
        },
      ],
      label: [styles.buttonLabel, { right: spacing / 2 }],
      bodyInfo: [
        styles.body,
        {
          padding: spacing,
          backgroundColor: accent,
        },
      ],
      submit: [
        styles.submit,
        {
          marginVertical: spacing / 2,
        },
      ],
      touchable: [styles.touchable, { backgroundColor: surface.textInput }],
      contactLink: [fontMaker({ weight: "Bold" })],
      icon: {
        tintColor: black,
      },
      body: [
        styles.body,
        {
          padding: spacing,
          backgroundColor: accent,
        },
      ],
      codeView: [fontMaker({ weight: "Bold" }), styles.codeView],
      buttonContent: [
        styles.buttonContent,
        {
          marginTop: spacing / 2,
        },
      ],
    }),
    [accent, spacing, surface.textInput, white, fontMaker, surface.background, black],
  )

  const renderIcon = useCallback(
    (name: string) =>
      ({
        name,
        category: "MaterialIcons",
        size: ICON_SIZE,
        color: black,
      } as VectorIconProps),
    [black],
  )

  const renderPathologyRightChild = useCallback(
    () => (
      <View style={styles.pathology}>
        {pathologyName ? (
          <Caption style={styles.caption} emphasis="medium">
            {pathologyName}
          </Caption>
        ) : null}
        {!isEdit ? <VectorIcon {...renderIcon("chevron-right")} /> : null}
      </View>
    ),
    [renderIcon, pathologyName, isEdit],
  )

  const onPress = useCallback(async () => {
    const subject = t(`${OperationNS}:contactTitle`)
    const body = t(`${OperationNS}:contactText`, {
      lastName: userData?.lastName,
      firstName: userData?.firstName,
      id: userDocRef?.id,
    })
    // noinspection ES6MissingAwait
    openUrl(
      `mailto:contact@doctup.fr?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
        body,
      )}`,
    )
    return
  }, [t, userData?.firstName, userData?.lastName, userDocRef?.id])

  // @ts-ignore
  const sections: Array<SectionListData<IRowItemProps<OperationParamList>, ISectionHeader>> =
    useMemo(
      () => [
        {
          shadows: 1,
          title: t("header"),
          headerStyle: s.header,
          data: [
            {
              name: "date",
              render: () => (
                <View style={s.rowDate}>
                  <DateTimePicker
                    testID="surgery-date-picker"
                    mode="date"
                    name="surgeryDate"
                    touchableStyle={s.touchable}
                    label={t("label.surgeryDate")}
                    initialValue={values.surgeryDate ?? currentDate}
                    noAdditionalValue
                    icons={{
                      left: renderIcon("calendar-today"),
                      right: renderIcon("arrow-drop-down"),
                    }}
                  />
                </View>
              ),
            },
          ],
        },
        {
          data: [
            {
              rowStyle: s.rowSubitem,
              name: t("label.pathology"),
              testID: "pathology-row",
              route: isEdit ? undefined : "Organ",
              rightChild: renderPathologyRightChild(),
            },
          ],
          title: "",
        },
        {
          data: [
            {
              name: t("label.pathology"),
              render: () => (
                <View style={s.rowPatInfo}>
                  {isEdit ? (
                    <View style={s.rowPatInfo}>
                      <View style={s.bodyInfo}>
                        <Body2>{t("pathologyEditInformation")}</Body2>
                        <Button
                          mode="text"
                          labelStyle={s.label}
                          onPress={onPress}
                          contentStyle={styles.buttonContent}
                        >
                          <Body2 style={s.contactLink} color={primary}>
                            {t("supportButton")}
                          </Body2>
                        </Button>
                      </View>
                    </View>
                  ) : null}
                </View>
              ),
            },
          ],
          title: "",
        },
        !isEdit
          ? {
              shadows: 1,
              data: [
                {
                  rowStyle: s.row,
                  name: (userData?.surgeonName as string) || t("missing"),
                  labelStyle: styles.label,
                  label: t("label.surgeon"),
                  leftChild: (
                    <Image style={s.icon} source={images.custom_surgery as ImageSourcePropType} />
                  ),
                },
              ],
            }
          : null,
        !isEdit
          ? {
              shadows: 1,
              data: [
                {
                  rowStyle: s.rowSubitem,
                  name: (userData?.kineName as string) || practicianRequestedText,
                  labelStyle: styles.label,
                  label: t("label.kine"),
                  leftChild: (
                    <Image style={s.icon} source={images.custom_surgery as ImageSourcePropType} />
                  ),
                },
                {
                  render: () => (
                    <>
                      {!userData?.kineName ? (
                        !requestedKine ? (
                          <View style={s.rowKine}>
                            <View style={s.body}>
                              <Body2>{t("findKine.text")}</Body2>
                              <Button
                                mode="text"
                                labelStyle={s.label}
                                onPress={() => navigation.navigate("KineList")}
                                contentStyle={styles.buttonContent}
                              >
                                <Body2 style={s.contactLink} color={primary}>
                                  {t("findKine.button")}
                                </Body2>
                              </Button>
                            </View>
                          </View>
                        ) : (
                          <View style={s.rowKine}>
                            <View style={s.body}>
                              <Body2>
                                {t("seeRequests.text", {
                                  ns: OperationNS,
                                  lastName: requestedKine?.lastName,
                                  firstName: requestedKine?.firstName,
                                })}
                              </Body2>

                              <Button
                                mode="text"
                                labelStyle={styles.buttonLabel}
                                onPress={() => navigation.navigate("KineRequestedScreen")}
                                contentStyle={styles.buttonContent}
                              >
                                <Body2 style={s.contactLink} color={primary}>
                                  {t("seeRequests.button")}
                                </Body2>
                              </Button>
                            </View>
                          </View>
                        )
                      ) : null}
                    </>
                  ),
                },
              ],
              title: "",
            }
          : null,
        !isEdit
          ? {
              shadows: 1,
              data: [
                {
                  rowStyle: s.rowSubitem,
                  name: (userData?.gpName as string) || t("missing"),
                  labelStyle: styles.label,
                  label: t("label.gp"),
                  leftChild: (
                    <Image style={s.icon} source={images.custom_surgery as ImageSourcePropType} />
                  ),
                },
              ],
              title: "",
            }
          : null,
      ],
      [
        isEdit,
        renderIcon,
        renderPathologyRightChild,
        s,
        t,
        onPress,
        primary,
        currentDate,
        values.surgeryDate,
        navigation,
        userData?.gpName,
        userData?.kineName,
        userData?.surgeonName,
        practicianRequestedText,
        requestedKine,
      ],
    )
  const filteredSections = sections.filter(item => item !== null)

  const onSubmit = useCallback(async () => {
    if (!startPhase || !endPhase) {
      showSnack({ message: t("noProgram") })
      crashlytics.reportError(
        23,
        `Aucune phase trouvée, date d'opération ${values.surgeryDate}, pathologie: ${values.pathology} `,
      )
      return
    }
    setSubmitLoading(true)
    handleSubmit?.()
    const programStart = firestore.Timestamp.fromMillis(
      dayjs(values.surgeryDate).add(startPhase.startDay, "day").toDate().getTime(),
    )
    const programEnd = firestore.Timestamp.fromMillis(
      dayjs(values.surgeryDate).add(endPhase.endDay, "day").toDate().getTime(),
    )

    if (!isEdit) {
      const newUserProgramValue = {
        phase: startPhase.id,
        programEnd,
        programStart,
      }
      await updateUserData(userDocRef, newUserProgramValue)
    }

    // If surgeryDate is updated, the phase can be updated too
    // Creation account, the initialization cannot be done without alert values
    if (isEdit && userData) {
      // Réinitialiser les jours additionnels pour les phases après la nouvelle phase de départ si la phase change
      if (initialStartPhaseId !== startPhase.id && !(endPhase?.id === currentPhase?.id)) {
        if (phases) {
          const newAddedDays = { ...userData.addedDays }
          const sortedPhases = [...phases].sort((a, b) => a.startDay - b.startDay)
          let reset = false
          sortedPhases.forEach(phase => {
            if (phase.id === startPhase.id) {
              reset = true
            }
            if (reset && phase.id !== startPhase.id && newAddedDays[phase.id]) {
              newAddedDays[phase.id] = 0
            }
          })
          await updateUserData(userDocRef, { addedDays: newAddedDays })
        }
        setInitialStartPhaseId(startPhase.id)
      }
      await updateUserData(userDocRef, { programEnd, programStart, phase: startPhase.id })
      setSubmitLoading(false)
      navigation.goBack()
    }
  }, [
    endPhase,
    handleSubmit,
    isEdit,
    navigation,
    showSnack,
    startPhase,
    t,
    userData,
    values.pathology,
    values.surgeryDate,
    userDocRef,
    initialStartPhaseId,
    phases,
    currentPhase?.id,
  ])

  const renderFooterComponent = useCallback(
    () => (
      <SubmitButton<OperationFormValues>
        testID="operation-submit-btn"
        {...{ onSubmit }}
        syncLoading={submitLoading}
        viewStyle={s.submit}
      />
    ),
    [onSubmit, submitLoading, s.submit],
  )

  return (
    <ParametersScreen
      listKey="operationList"
      sections={filteredSections}
      footerComponent={renderFooterComponent()}
    />
  )
}

const styles = StyleSheet.create({
  submit: {
    width: "100%",
  },
  row: {
    height: INPUT_HEIGHT,
  },
  touchable: {
    height: INPUT_HEIGHT,
    borderRadius: 0,
  },
  shadow: generateShadow(2) as ViewStyle,
  body: {
    borderRadius: 8,
    overflow: "hidden",
  },
  pathology: {
    flexDirection: "row",
    alignItems: "center",
  },
  caption: {
    width: 150,
  },
  buttonContent: {
    flexDirection: "row-reverse",
  },
  buttonLabel: {
    marginHorizontal: 0,
    alignItems: "flex-start",
  },
  label: {
    paddingBottom: 0,
  },
  codeView: {
    textAlign: "center",
  },
})

export default OperationScreen
